import {
    DEFAULT_CURRENCY,
    DEFAULT_QUANTITY,
    normalizeCommitmentType,
    normalizeNumber,
    normalizeSegment,
} from '../../core/normalizers';
import { GTMItemType, GTMOrderType } from '../../types';
import type { TrackingCommerce, TrackingItem } from '../../types';
import type {
    FixPlanEventData,
    FixPlanSource,
    PlanChangeLeadModalEventData,
    RatePlanEventData,
    RatePlanSource,
    ServicePlanEventData,
    ServicePlanLeadModalEventData,
    ServicePlanSource,
} from './types';

const getPlanListName = (planType?: string) => (planType || '').toUpperCase();

const getPlanVariant = (description?: string, variantSuffix?: string) =>
    [(description ?? '').trim(), (variantSuffix ?? '').trim()].filter(Boolean).join(' + ');

type ServicePlanCommerceParams<Plan extends ServicePlanSource> = {
    commitmentTypeId?: string;
    getListId?: (plan: Plan) => string | undefined;
    getVariant: (plan: Plan) => string;
    planType: string;
    plans: Plan[];
    segment?: string;
};

const mapServicePlanToTrackingItem = <Plan extends ServicePlanSource>({
    commitmentType,
    getListId,
    getVariant,
    index,
    itemListName,
    plan,
}: {
    commitmentType: string;
    getListId?: (plan: Plan) => string | undefined;
    getVariant: (plan: Plan) => string;
    index: number;
    itemListName: string;
    plan: Plan;
}): TrackingItem => {
    const price = normalizeNumber(plan.pricing?.new || plan.pricing?.regular);

    return {
        type: GTMItemType.Product,
        id: plan.name?.toLowerCase() || '',
        name: plan.name || '',
        brand: 'Telia',
        variant: getVariant(plan),
        category: itemListName,
        listId: getListId?.(plan) || '',
        listName: itemListName,
        index,
        originalPrice: normalizeNumber(plan.pricing?.old) || price,
        price,
        discount: 0,
        quantity: DEFAULT_QUANTITY,
        commitmentType,
    };
};

const mapServicePlansToTrackingCommerce = <Plan extends ServicePlanSource>({
    commitmentTypeId,
    getListId,
    getVariant,
    planType,
    plans,
    segment,
}: ServicePlanCommerceParams<Plan>): TrackingCommerce => {
    const itemListName = getPlanListName(planType);
    const commitmentType = normalizeCommitmentType(commitmentTypeId);

    return {
        orderType: GTMOrderType.Services,
        segment: normalizeSegment(segment),
        currency: DEFAULT_CURRENCY,
        value: 0,
        itemListName,
        items: plans.map((plan, index) =>
            mapServicePlanToTrackingItem({
                commitmentType,
                getListId,
                getVariant,
                index,
                itemListName,
                plan,
            }),
        ),
    };
};

export const mapRatePlansToTrackingCommerce = ({
    commitmentTypeId,
    planType,
    plans,
    segment,
}: RatePlanEventData): TrackingCommerce =>
    mapServicePlansToTrackingCommerce<RatePlanSource>({
        commitmentTypeId,
        getListId: (plan) => plan.cartGuid,
        getVariant: (plan) => getPlanVariant(plan.description, plan.mobileData?.new || plan.mobileData?.regular),
        planType,
        plans,
        segment,
    });

export const mapFixPlansToTrackingCommerce = ({
    commitmentTypeId,
    plans,
    segment,
}: FixPlanEventData): TrackingCommerce =>
    mapServicePlansToTrackingCommerce<FixPlanSource>({
        commitmentTypeId,
        getVariant: (plan) => getPlanVariant(plan.description, plan.internetSpeed),
        planType: 'fix',
        plans,
        segment,
    });

export const mapServicePlanEventToTrackingCommerce = (data: ServicePlanEventData): TrackingCommerce =>
    mapServicePlansToTrackingCommerce({
        commitmentTypeId: data.commitmentTypeId,
        getListId: (plan) => plan.cartGuid,
        getVariant: (plan) =>
            getPlanVariant(plan.description, plan.internetSpeed || plan.mobileData?.new || plan.mobileData?.regular),
        planType: data.planType,
        plans: data.plans,
        segment: data.segment,
    });

export const mapServicePlanLeadModalToTrackingCommerce = ({
    segment,
    planType,
    plan,
    planId,
    planName,
    pricing,
    customId,
    commitmentType,
    commitmentName,
}: ServicePlanLeadModalEventData): TrackingCommerce => {
    const itemIndex = parseInt(customId?.split('-').pop() || '', 10);
    const commerce = mapServicePlansToTrackingCommerce({
        commitmentTypeId: commitmentType,
        getListId: () => plan?.cartGuid,
        getVariant: () => getPlanVariant(plan?.description, plan?.mobileData?.new || plan?.mobileData?.regular),
        planType,
        plans: [{ name: planName, pricing }],
        segment,
    });

    return {
        ...commerce,
        items: commerce.items?.map((item) => ({
            ...item,
            commitmentType: commitmentName || item.commitmentType,
            id: planId || planName?.toLowerCase() || '',
            index: Number.isNaN(itemIndex) ? 0 : itemIndex,
        })),
    };
};

export const mapPlanChangeLeadModalToTrackingCommerce = ({
    segment,
    planType,
    plan,
    commitmentType,
}: PlanChangeLeadModalEventData): TrackingCommerce => {
    const itemListName = getPlanListName(planType);
    const price = normalizeNumber(plan?.pricing?.new || plan?.pricing?.regular);
    const itemIndex = Number.parseInt(plan?.customId?.split('-').pop() || '', 10);

    return {
        orderType: GTMOrderType.Services,
        segment: normalizeSegment(segment),
        currency: DEFAULT_CURRENCY,
        value: price,
        itemListName,
        items: [
            {
                type: GTMItemType.Product,
                id: plan?.personalOfferId || plan?.name?.toLowerCase() || plan?.cartGuid || '',
                name: plan?.name || '',
                brand: 'Telia',
                variant: getPlanVariant(plan?.description, plan?.mobileData?.new || plan?.mobileData?.regular),
                category: itemListName,
                listId: plan?.cartGuid || '',
                listName: itemListName,
                index: Number.isNaN(itemIndex) ? 0 : itemIndex,
                originalPrice: normalizeNumber(plan?.pricing?.old) || price,
                price,
                discount: 0,
                quantity: DEFAULT_QUANTITY,
                commitmentType: normalizeCommitmentType(commitmentType),
            },
        ],
    };
};
